import type { CSSProperties, ReactNode } from 'react'
import { SHAPE_SIZE, type UnitShape } from '@/lib/unitShapes'

/** The colours a unit is drawn in: its state decides. */
export type UnitTone = 'normal' | 'lit' | 'focus' | 'down' | 'picked'

const TONES: Record<UnitTone, { top: string; front: string; side: string; trim: string }> = {
  normal: { top: 'rgb(226 232 240)', front: 'rgb(203 213 225)', side: 'rgb(148 163 184)', trim: 'rgb(100 116 139)' },
  lit: { top: 'rgb(191 219 254)', front: 'rgb(147 197 253)', side: 'rgb(96 165 250)', trim: 'rgb(37 99 235)' },
  focus: { top: 'rgb(96 165 250)', front: 'rgb(37 99 235)', side: 'rgb(30 64 175)', trim: 'rgb(219 234 254)' },
  down: { top: 'rgb(253 230 138)', front: 'rgb(251 191 36)', side: 'rgb(217 119 6)', trim: 'rgb(146 64 14)' },
  picked: { top: 'rgb(167 243 208)', front: 'rgb(52 211 153)', side: 'rgb(5 150 105)', trim: 'rgb(6 95 70)' },
}

const face = (s: CSSProperties): CSSProperties => ({ position: 'absolute', pointerEvents: 'none', ...s })

/**
 * A solid block `w` × `d` on the floor, `h` tall, from `z` up: top and four
 * sides, so it reads right from any angle the camera turns to. `front` is
 * drawn on the face towards the viewer's default side (the room's front).
 */
function Cuboid({
  x = 0,
  y = 0,
  z = 0,
  w,
  d,
  h,
  tone,
  front,
  alpha = 1,
}: {
  x?: number
  y?: number
  z?: number
  w: number
  d: number
  h: number
  tone: (typeof TONES)[UnitTone]
  front?: ReactNode
  alpha?: number
}) {
  return (
    <div style={face({ left: x, top: y, width: w, height: d, transformStyle: 'preserve-3d', transform: `translateZ(${z}px)`, opacity: alpha === 1 ? undefined : alpha })}>
      <div style={face({ inset: 0, backgroundColor: tone.top, transform: `translateZ(${h}px)`, outline: `1px solid ${tone.side}` })} />
      {/* Front (towards the room's front wall) carries the detail. */}
      {/* Stood up about its top edge, so its contents would read upside down: flipped back. */}
      <div style={face({ left: 0, top: d, width: w, height: h, backgroundColor: tone.front, transformOrigin: 'top', transform: 'rotateX(90deg)', outline: `1px solid ${tone.side}` })}>
        <div style={{ position: 'absolute', inset: 0, transform: 'scaleY(-1)' }}>{front}</div>
      </div>
      <div style={face({ left: 0, top: 0, width: w, height: h, backgroundColor: tone.side, transformOrigin: 'top', transform: 'rotateX(90deg)' })} />
      <div style={face({ left: w, top: 0, width: h, height: d, backgroundColor: tone.side, transformOrigin: 'left', transform: 'rotateY(-90deg)' })} />
      <div style={face({ left: 0, top: 0, width: h, height: d, backgroundColor: tone.front, transformOrigin: 'left', transform: 'rotateY(-90deg)' })} />
    </div>
  )
}

/**
 * A unit drawn as what it is (z8nrz7fw25): a rack is an open frame with
 * shelves, an LN2 tank a cylinder with a lid, a freezer or fridge a tall
 * cabinet with a door, a bench a top on legs. `cell` is the view's scale —
 * pixels per cell — so the same model serves the floor and the room views.
 */
export function UnitModel({ shape, cell, tone, x, y }: { shape: UnitShape; cell: number; tone: UnitTone; x: number; y: number }) {
  const size = SHAPE_SIZE[shape]
  const w = size.w * cell
  const d = size.d * cell
  const h = size.h * cell * 0.5
  const t = TONES[tone]

  return (
    <div style={face({ left: x, top: y, width: w, height: d, transformStyle: 'preserve-3d' })} aria-hidden>
      {shape === 'rack' && <Rack w={w} d={d} h={h} tone={t} />}
      {shape === 'tank' && <Tank w={w} h={h} tone={t} />}
      {shape === 'bench' && <Bench w={w} d={d} h={h} tone={t} />}
      {(shape === 'freezer' || shape === 'fridge' || shape === 'cabinet' || shape === 'box') && (
        <Cuboid
          w={w}
          d={d}
          h={h}
          tone={t}
          front={
            shape === 'box' ? null : shape === 'cabinet' ? (
              <>
                <div style={{ position: 'absolute', left: '50%', top: '8%', bottom: '8%', width: 1, backgroundColor: t.trim }} />
                <div style={{ position: 'absolute', left: '42%', top: '45%', width: 2, height: '10%', backgroundColor: t.trim }} />
                <div style={{ position: 'absolute', left: '56%', top: '45%', width: 2, height: '10%', backgroundColor: t.trim }} />
              </>
            ) : (
              <>
                {shape === 'fridge' && <div style={{ position: 'absolute', inset: '10% 14%', backgroundColor: 'rgb(224 242 254 / 0.85)', border: `1px solid ${t.trim}` }} />}
                <div style={{ position: 'absolute', right: '12%', top: '30%', width: 2, height: '25%', backgroundColor: t.trim }} />
                {shape === 'freezer' && <div style={{ position: 'absolute', left: '8%', right: '8%', top: '62%', height: 1, backgroundColor: t.trim }} />}
              </>
            )
          }
        />
      )}
    </div>
  )
}

/** An open frame: four posts and shelves you can see between. */
function Rack({ w, d, h, tone }: { w: number; d: number; h: number; tone: (typeof TONES)[UnitTone] }) {
  const post = Math.max(2, Math.round(Math.min(w, d) * 0.08))
  const shelves = 4
  return (
    <>
      {[
        [0, 0],
        [w - post, 0],
        [0, d - post],
        [w - post, d - post],
      ].map(([px, py]) => (
        <Cuboid key={`${px}:${py}`} x={px} y={py} w={post} d={post} h={h} tone={{ ...tone, top: tone.trim, front: tone.trim, side: tone.trim }} />
      ))}
      {Array.from({ length: shelves }, (_, i) => (
        <Cuboid key={i} z={(h / shelves) * i + 2} w={w} d={d} h={2} tone={tone} />
      ))}
    </>
  )
}

/** A cylinder of 12 faces with a round lid. */
function Tank({ w, h, tone }: { w: number; h: number; tone: (typeof TONES)[UnitTone] }) {
  const sides = 12
  const r = w / 2
  const facet = 2 * r * Math.tan(Math.PI / sides) + 1
  return (
    <div style={face({ left: r, top: r, width: 0, height: 0, transformStyle: 'preserve-3d' })}>
      {Array.from({ length: sides }, (_, i) => (
        <div
          key={i}
          style={face({
            left: -facet / 2,
            top: 0,
            width: facet,
            height: h,
            transformOrigin: 'top center',
            transform: `rotateZ(${(360 / sides) * i}deg) translateY(${r}px) rotateX(90deg)`,
            backgroundColor: i % 2 ? tone.front : tone.side,
          })}
        />
      ))}
      <div style={face({ left: -r, top: -r, width: 2 * r, height: 2 * r, borderRadius: '50%', backgroundColor: tone.top, border: `2px solid ${tone.trim}`, transform: `translateZ(${h}px)` })} />
      <div style={face({ left: -r * 0.35, top: -r * 0.35, width: r * 0.7, height: r * 0.7, borderRadius: '50%', backgroundColor: tone.trim, transform: `translateZ(${h + 1}px)` })} />
    </div>
  )
}

/** A worktop on four legs, something on top of it. */
function Bench({ w, d, h, tone }: { w: number; d: number; h: number; tone: (typeof TONES)[UnitTone] }) {
  const leg = Math.max(2, Math.round(d * 0.1))
  const top = Math.max(3, Math.round(h * 0.12))
  return (
    <>
      {[
        [0, 0],
        [w - leg, 0],
        [0, d - leg],
        [w - leg, d - leg],
      ].map(([px, py]) => (
        <Cuboid key={`${px}:${py}`} x={px} y={py} w={leg} d={leg} h={h - top} tone={{ ...tone, top: tone.trim, front: tone.trim, side: tone.trim }} />
      ))}
      <Cuboid z={h - top} w={w} d={d} h={top} tone={tone} />
      <Cuboid x={w * 0.3} y={d * 0.2} z={h} w={w * 0.4} d={d * 0.6} h={h * 0.45} tone={tone} />
    </>
  )
}
